// @flow
/* eslint-disable ta-code-conventions/has-matching-test */

import * as React from 'react';
import { localize } from '@ta/common.i18n';
import classNames from '@ta/common.classnames';
import * as keyboardCodes from '@ta/common.keyboardCodes';
import { ManagedTextInput } from '@ta/input.text-input';
import Window from '@ta/events.window';

import Typeahead from '../Typeahead';
import type { FetcherResultsMap } from '../Typeahead';
import TypeaheadTextInput from '../TypeaheadTextInput';
import TypeaheadTracking from '../TypeaheadTracking';
import type { TypeaheadResult, TypeaheadResultType } from '../TypeaheadResult';

import styles from './Selector.less';

type Props = {|
  /** The text to display in the text input */
  typeaheadTextInputValue: string,
  /** Callback triggered when the selection changes. */
  selectionChanged: TypeaheadResult => void,
  /** Callback triggered when the text input changes. */
  textInputChanged: string => void,
  /** The fetchers passed in as a prop */
  fetchers: Array<any>,
  /** Placeholder text to display */
  placeHolder?: ?string,
  /** A CSS class name to apply to the <input/> */
  className?: string,
  /** Overrides the type that is applied to the <input/>. */
  inputType?: string,
  /** Optionally change the icon of the selector.  Defaults to 'search' */
  iconType?: string,
  /** Optionally specify the icon color (green, or default gray) in the selector. */
  greenIcon?: boolean,
  /** Callback for when the typeahead input gains focus. */
  onFocus?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Callback for when the typeahead input loses focus. */
  onBlur?: (e: SyntheticEvent<HTMLInputElement>) => void,
  /** Children for rendering results */
  children: (
    results: FetcherResultsMap,
    tracker: TypeaheadTracking,
    onSelection: (
      event: SyntheticEvent<HTMLElement> | KeyboardEvent,
      result: TypeaheadResult,
    ) => void,
  ) => React.Node,
  /** If handleEmptyResult is true, a "No result" message will display when search returns no result */
  handleEmptyResult?: boolean,
  /** Optionally specify the typeahead item type, default to be "location_id" */
  itemType: TypeaheadResultType,
  /** Callback to grab a ref to the text input */
  onRef: (textInput: ?HTMLInputElement) => mixed,
  /** Decide if dropdown will be closed when click outside the typeahead */
  closeDropdownOnClickOutside?: boolean,
  /** Position the typeahead results above the input field */
  resultsAbove?: boolean,
|};

type State = {|
  /** When closed is true, the dropdown elements will not be rendered (even when there're matching results) */
  closed: boolean,
  /** Tracks if input field is focused */
  inputFocused: boolean,
|};

const isEmptySafe = (array: any) => !array || array.length === 0;

/**
 * Component that lets you pick a geo/locations with typeahead support.
 */
class Selector extends React.Component<Props, State> {
  static defaultProps = {
    placeHolder: null,
    className: undefined,
    inputType: undefined,
    iconType: 'search',
    greenIcon: false,
    onFocus: undefined,
    onBlur: undefined,
    itemType: 'location_id',
    onRef: () => {},
    closeDropdownOnClickOutside: true,
    resultsAbove: false,
  };

  state = {
    closed: false,
    inputFocused: false,
  };

  componentDidMount() {
    this.isMounted = true;
  }

  componentWillUnmount() {
    this.isMounted = false;
  }

  onSelection = (event: SyntheticEvent<HTMLElement> | KeyboardEvent, result: TypeaheadResult) => {
    this.setState({ closed: false });
    this.setSelection(result);
  };

  onFocus = (event: SyntheticEvent<HTMLInputElement>) => {
    this.setState({ closed: false, inputFocused: true });
    if (this.props.onFocus) {
      this.props.onFocus(event);
    }
  };

  onBlur = (event: SyntheticEvent<HTMLInputElement>) => {
    this.setState({ inputFocused: false });
    if (this.props.onBlur) {
      this.props.onBlur(event);
    }
  };

  setSelection = (result: TypeaheadResult) => {
    if (result) {
      this.updateSearch('');
      if (this.textInput) {
        this.textInput.focus();
      }
      this.props.selectionChanged(result);
    }
  };

  handleOnKeyDown = (event: KeyboardEvent) => {
    if (event.keyCode === keyboardCodes.KEY_TAB && this.props.closeDropdownOnClickOutside) {
      this.setState({ closed: true });
    }
  };

  // TODO update when tracking requirements are available
  tracker = new TypeaheadTracking('', '');
  textInput: ?HTMLInputElement;
  updateSearch: string => void;
  isMounted = false;

  render() {
    const placeHolder = this.props.placeHolder || localize('common_Search');

    return (
      <React.Fragment>
        <Typeahead fetchers={this.props.fetchers}>
          {(results, updateSearch) => {
            this.updateSearch = updateSearch;
            // don't display empty result divs at the bottom
            const hasResults =
              results && Object.keys(results).filter(key => !isEmptySafe(results[key])).length > 0;
            // Empty result, will display "Empty Result" element at the bottom, apply for search query returns empty
            const showEmptyResult =
              this.props.handleEmptyResult &&
              results.typeaheadjson &&
              !results.typeaheadjson.length;

            return (
              <div className={styles.textInput} onKeyDown={this.handleOnKeyDown}>
                <span
                  className={classNames('ui_icon', this.props.iconType, styles.icon, {
                    [styles.accessibleGreenIcon]: this.props.greenIcon,
                  })}
                />
                <TypeaheadTextInput
                  inputComponentType={ManagedTextInput}
                  inputComponentClassName={classNames(
                    'ui_input_text',
                    styles.socialInput,
                    this.props.className,
                  )}
                  value={this.props.typeaheadTextInputValue}
                  type={this.props.inputType}
                  onChange={(newValue: string) => {
                    this.setState({ closed: false });
                    this.props.textInputChanged(newValue);
                  }}
                  onQueryChange={(newValue: string) => {
                    updateSearch(newValue);
                  }}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  searchDelay={50}
                  placeholder={placeHolder}
                  onRef={textInput => {
                    this.textInput = textInput;
                    this.props.onRef(textInput);
                  }}
                />
                {(hasResults || showEmptyResult) && !this.state.closed && (
                  <div
                    className={classNames(styles.results, {
                      [styles.resultsAbove]: this.props.resultsAbove,
                    })}
                  >
                    {this.props.children(results, this.tracker, this.onSelection)}
                  </div>
                )}
              </div>
            );
          }}
        </Typeahead>
        <Window
          event="click"
          callback={() => {
            /** This Window-level click listener provides the option of closing a dropdown when a click happens outside the component, which is not the current default behavior.
             *  There are two cases ('onBlur' always happens before 'onclick'):
             *  1. if 'closeDropdownOnClickOutside' is set, we handle the 'Close dropdown' here (similarly to 'onBlur')
             *  2. if there was a click on a menu item, it will have been handled in the onSelection() handler, and when it bubbles up here it can be ignored by checking the inputFocused state
             */
            if (
              this.isMounted &&
              this.props.closeDropdownOnClickOutside &&
              !this.state.inputFocused
            ) {
              this.setState({ closed: true });
            }
          }}
        />
      </React.Fragment>
    );
  }
}

export default Selector;
